import type {
  CourseSession,
  CourseSessionAttendee,
  CourseSessionDateBlock,
  Customer,
  SafePassCardTrackingStatus,
  SafePassProcessStatus,
} from '../types/crm';

export const PROCESS_NUMBER_HOURS_THRESHOLD = 96;

export const PROCESS_STATUS_OPTIONS: Array<{ value: SafePassProcessStatus; label: string }> = [
  { value: 'not_required', label: 'Not required' },
  { value: 'required', label: 'Required' },
  { value: 'sent_to_trainer', label: 'Send to trainer' },
  { value: 'received', label: 'Received' },
  { value: 'denied', label: 'Denied' },
];

export const CARD_STATUS_OPTIONS: Array<{ value: SafePassCardTrackingStatus; label: string }> = [
  { value: 'not_received', label: 'Not received' },
  { value: 'received', label: 'Received' },
  { value: 'posted', label: 'Posted' },
];

export function isSafePassCourse(courseName: string | undefined | null): boolean {
  const normalized = String(courseName || '').toLowerCase();
  return normalized.includes('safe pass') || normalized.includes('safepass');
}

export function getSessionStartDate(session: CourseSession): string | null {
  const dates = (session.dateBlocks || [])
    .map((b) => String(b.date || '').slice(0, 10))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    .sort();
  return dates[0] || null;
}

export function formatDateUk(iso: string | null | undefined): string {
  if (!iso) return '—';
  const part = String(iso).slice(0, 10);
  const m = part.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return part;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

export function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function normalizeBlockTime(time: string | null | undefined, fallback: string): string {
  const t = String(time || '').trim();
  if (!t) return fallback;
  if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) return t;
  return fallback;
}

function parseLocalDateTime(isoDate: string, time: string): number | null {
  const ms = new Date(`${isoDate}T${time}`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Earliest date block on the session (by date, then start time). */
export function getFirstDateBlock(session: CourseSession): CourseSessionDateBlock | null {
  const blocks = (session.dateBlocks || [])
    .map((b) => ({
      ...b,
      date: String(b.date || '').slice(0, 10),
    }))
    .filter((b) => /^\d{4}-\d{2}-\d{2}$/.test(b.date))
    .sort((a, b) => {
      const byDate = a.date.localeCompare(b.date);
      if (byDate !== 0) return byDate;
      return normalizeBlockTime(a.startTime, '00:00:00').localeCompare(
        normalizeBlockTime(b.startTime, '00:00:00')
      );
    });
  return blocks[0] || null;
}

export function hoursUntilSessionStart(session: CourseSession, now = new Date()): number | null {
  const block = getFirstDateBlock(session);
  if (!block) return null;
  const startTime = normalizeBlockTime(block.startTime, '00:00:00');
  const startMs = parseLocalDateTime(block.date, startTime);
  if (startMs == null) return null;
  return (startMs - now.getTime()) / (1000 * 60 * 60);
}

export function isWithinProcessNumberAlertWindow(session: CourseSession, now = new Date()): boolean {
  if (session.status === 'cancelled') return false;
  const block = getFirstDateBlock(session);
  if (!block) return false;

  const startDate = block.date;
  const today = now.toISOString().slice(0, 10);

  // Same calendar day as the course — alert until the block end time (or end of day).
  if (startDate === today) {
    const endTime = normalizeBlockTime(block.endTime, '23:59:59');
    const endMs = parseLocalDateTime(startDate, endTime);
    if (endMs != null && now.getTime() <= endMs) return true;
  }

  const hours = hoursUntilSessionStart(session, now);
  if (hours == null) return false;
  return hours >= 0 && hours < PROCESS_NUMBER_HOURS_THRESHOLD;
}

export function attendeeNeedsProcessAction(attendee: CourseSessionAttendee): boolean {
  if (attendee.status !== 'booked' || attendee.isVirtual) return false;
  const status = attendee.safePassProcessStatus || 'not_required';
  return status === 'required' || status === 'sent_to_trainer';
}

export function sessionNeedsProcessAction(
  session: CourseSession,
  attendees: CourseSessionAttendee[],
  now = new Date()
): boolean {
  if (!isSafePassCourse(session.courseName)) return false;
  if (!isWithinProcessNumberAlertWindow(session, now)) return false;
  return attendees.some(attendeeNeedsProcessAction);
}

export function countSessionsNeedingProcessAction(
  sessions: CourseSession[],
  attendeesBySession: Record<string, CourseSessionAttendee[]>,
  now = new Date()
): number {
  return sessions.filter((session) => {
    const attendees = attendeesBySession[session.id] || [];
    return sessionNeedsProcessAction(session, attendees, now);
  }).length;
}

export function getProcessStatusLabel(status: SafePassProcessStatus | undefined | null): string {
  return PROCESS_STATUS_OPTIONS.find((o) => o.value === status)?.label || 'Not required';
}

export function getCardStatusLabel(status: SafePassCardTrackingStatus | undefined | null): string {
  return CARD_STATUS_OPTIONS.find((o) => o.value === status)?.label || 'Not received';
}

export function inferProcessStatus(attendee: CourseSessionAttendee): SafePassProcessStatus {
  const stored = attendee.safePassProcessStatus;
  if (
    stored === 'required' ||
    stored === 'sent_to_trainer' ||
    stored === 'denied' ||
    stored === 'received'
  ) {
    return stored;
  }
  const pn = String(attendee.safePassProcessNumber || '').trim();
  if (pn) return 'received';
  return stored || 'not_required';
}

export function isUrgentProcessStatus(status: SafePassProcessStatus | undefined | null): boolean {
  return status === 'required' || status === 'sent_to_trainer';
}

export function getAttendeeDisplayName(
  attendee: CourseSessionAttendee,
  customer?: Customer | null
): string {
  const enrolled = [attendee.enrollmentFirstName, attendee.enrollmentSurname]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' ');
  if (enrolled) return enrolled;
  if (customer) {
    const person = [customer.firstName, customer.surname].map((v) => String(v || '').trim()).filter(Boolean).join(' ');
    if (person) return person;
    if (customer.companyName) return customer.companyName;
  }
  if (attendee.isVirtual) {
    const label = String(attendee.placeholderLabel || '').trim() || 'Reserved';
    return `Reserved — ${label}`;
  }
  if (attendee.placeholderLabel) return attendee.placeholderLabel;
  return 'Participant';
}

export function getAttendeeCompanyName(
  attendee: CourseSessionAttendee,
  customersById: Record<string, Customer>
): string {
  const companyId =
    attendee.sponsoringCompanyCustomerId ||
    attendee.companyCustomerId ||
    (attendee.payerType === 'company' ? attendee.payerCustomerId : null);
  if (companyId && customersById[companyId]) {
    return customersById[companyId].companyName || '—';
  }
  const individualId = attendee.individualCustomerId;
  if (individualId && customersById[individualId]?.customerType === 'company') {
    return customersById[individualId].companyName || '—';
  }
  return '—';
}

export function sessionOverlapsDateRange(
  session: CourseSession,
  fromIso: string,
  toIso: string
): boolean {
  const start = getSessionStartDate(session);
  if (!start) return false;
  return start >= fromIso && start <= toIso;
}

export function defaultDateRange(): { from: string; to: string } {
  const from = new Date();
  const to = new Date();
  to.setDate(to.getDate() + 60);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}